import React, { useState } from 'react';
import './SuggestionChips.css';

interface SuggestedPrompt {
  text: string;   // Button text (e.g., "Show me jobs")
  query: string;  // Actual query to send (e.g., "jobs")
  icon: string;   // Emoji icon for the button
}

interface SuggestionChipsProps {
  suggestions: SuggestedPrompt[];
  onSelectSuggestion: (query: string) => void;
  disabled?: boolean;
}

export const SuggestionChips: React.FC<SuggestionChipsProps> = ({ 
  suggestions, 
  onSelectSuggestion,
  disabled = false
}) => {
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // Validate and filter suggestions
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const validSuggestions = suggestions.filter(s => 
    s.text && s.query && s.icon
  );

  if (validSuggestions.length === 0) {
    return null;
  }

  const handleClick = (query: string, index: number) => {
    if (disabled) return;
    
    setClickedIndex(index);
    onSelectSuggestion(query);
    
    // Reset after animation
    setTimeout(() => setClickedIndex(null), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number, query: string) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleClick(query, index);
        break;
      case 'ArrowRight':
        e.preventDefault();
        setFocusedIndex((index + 1) % validSuggestions.length);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedIndex((index - 1 + validSuggestions.length) % validSuggestions.length);
        break;
    }
  };

  return (
    <div 
      className="suggestion-chips"
      role="group"
      aria-label="Suggested follow-up questions"
    >
      <p className="suggestion-label" id="chips-label">💡 Try asking:</p>
      <div 
        className="chips-container"
        role="list"
        aria-labelledby="chips-label"
      >
        {validSuggestions.map((suggestion, index) => (
          <button
            key={index}
            role="listitem"
            className={`chip ${clickedIndex === index ? 'clicked' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => handleClick(suggestion.query, index)}
            onKeyDown={(e) => handleKeyDown(e, index, suggestion.query)}
            tabIndex={0}
            disabled={disabled}
            aria-label={`Ask: ${suggestion.text}`}
            aria-busy={clickedIndex === index}
            ref={el => {
              if (el && focusedIndex === index) {
                el.focus();
              }
            }}
          >
            {clickedIndex === index && (
              <span className="chip-loader" aria-hidden="true">⏳</span>
            )}
            <span className="chip-icon" aria-hidden="true">{suggestion.icon}</span>
            <span className="chip-text">{suggestion.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SuggestionChips;












